import type { PAGE_POSITIONS } from 'types';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import ExternalIcon from '~/assets/icons/ExternalIcon';
import ProductNotificationCount from '~/components/ProductNotificationCount';
import ProductClickedEvent from '~/events/ProductClickedEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import ClickOutLink from './ClickOutLink';
import Image from './Image';
import Price from './Price';
import ProductNotificationButton from './ProductNotificationButton';
import StockStatus from './StockStatus';
import Tracked from './Tracked';

type Props = {
  allowNotifications: boolean;
  cardSize: 'small' | 'large';
  className?: string;
  imageLoading?: 'lazy' | 'eager';
  overrideInteractionPagePosition?: boolean;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  showNotificationCount?: boolean;
  withMetadata?: boolean;
};

interface ExternalProps extends Props {
  openPdp?: boolean;
  product: ProductListItemExternal;
}

interface InternalProps extends Props {
  name?: TrackedButtonClickName;
  onClick?: () => void;
  product: ProductListItemInternal;
}

const ExternalProductCard = ({
  allowNotifications,
  cardSize,
  className,
  imageLoading,
  openPdp = true,
  overrideInteractionPagePosition = false,
  padImage,
  pagePosition,
  product,
  showNotificationCount = false,
  withMetadata
}: ExternalProps) => {
  return (
    <div className={`relative ${className}`}>
      <ClickOutLink
        affiliate={product.affiliate}
        buttonPosition={0}
        pagePosition={pagePosition}
        price={product.price}
        product={product}
        urlTemplates={product}
        variant="none"
        className="flex cursor-pointer flex-col justify-center gap-2 overflow-hidden"
        openPdp={openPdp}
      >
        <CardContents
          allowNotifications={allowNotifications}
          withMetadata={withMetadata}
          cardSize={cardSize}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          showNotificationCount={showNotificationCount}
        />
      </ClickOutLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        pagePosition={
          overrideInteractionPagePosition ? pagePosition : 'Product Card'
        }
      />
    </div>
  );
};

const InternalProductCard = ({
  allowNotifications,
  name = 'PRODUCT_CARD',
  pagePosition,
  product,
  padImage,
  cardSize,
  className,
  onClick,
  imageLoading,
  showNotificationCount = false,
  overrideInteractionPagePosition = false
}: InternalProps) => {
  const user = useUser();
  const { isBot } = useRootData();

  return (
    <div className={`relative ${className}`}>
      <Tracked.InternalLink
        data-cy="product-card"
        title={product.name}
        aria-label={product.name}
        to={product.path}
        onClick={() => {
          new ProductClickedEvent({ isBot, product, user }).send();

          if (onClick) onClick();
        }}
        className="flex cursor-pointer flex-col justify-center gap-2 overflow-hidden"
        variant="none"
        name={name}
        pagePosition={pagePosition}
      >
        <CardContents
          allowNotifications={allowNotifications}
          cardSize={cardSize}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          showNotificationCount={showNotificationCount}
        />
      </Tracked.InternalLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        pagePosition={
          overrideInteractionPagePosition ? pagePosition : 'Product Card'
        }
      />
    </div>
  );
};

function CardContents({
  product,
  cardSize,
  padImage,
  imageLoading,
  showNotificationCount,
  withMetadata = true
}: Props & { product: ProductListItemExternal | ProductListItemInternal }) {
  return (
    <>
      <div className="shadow-sm hover:opacity-80">
        <div className="absolute left-2 top-2 lg:left-4 lg:top-4">
          <StockStatus
            status={product.stock_status}
            releaseDate={product.date.released}
            releaseDateFormat={cardSize === 'large' ? 'long' : 'short'}
          />
        </div>
        <ProductImage
          alt={product.image?.alt}
          url={product.image?.url}
          padImage={padImage}
          cardSize={cardSize}
          imageLoading={imageLoading}
        />
        {showNotificationCount && product.weekly_notifications_count && (
          <div className="mt-2 bg-white/90">
            <ProductNotificationCount
              count={product.weekly_notifications_count}
            />
          </div>
        )}
      </div>

      {withMetadata && (
        <div className="flex h-20 flex-col gap-2">
          <h4
            data-cy="product-name"
            className="line-clamp-2 text-left text-sm font-semibold"
          >
            {product.name}
          </h4>
          <div className="flex flex-row items-center gap-2">
            <Price
              regular={product.price.regular}
              sale={product.price.sale}
              currency={product.price.currency}
              className="gap-2 text-sm"
            />
            {product.type === 'external' && (
              <div className="flex flex-row gap-1">
                <ExternalIcon />
                <h4 className="text-sm text-grey-hide">
                  {product.affiliate.name}
                </h4>
              </div>
            )}
          </div>
          {product.sub_type === 'clothing' && (
            <div className="text-sm font-light text-grey-hide">{`${product.num_of_variants} colour${product.num_of_variants === 1 ? '' : 's'}`}</div>
          )}
        </div>
      )}
    </>
  );
}

const ProductImage = ({
  alt,
  url,
  padImage,
  cardSize,
  imageLoading
}: {
  alt?: string;
  cardSize: 'small' | 'large';
  imageLoading: Props['imageLoading'];
  padImage: boolean;
  url?: string;
}) => {
  if (!url) return <SquareImageSkeleton />;

  const imageSize = cardSize === 'large' ? 768 : 576;

  return (
    <SquareImageSkeleton>
      <Image
        alt={alt}
        src={url}
        height={imageSize}
        width={imageSize}
        pad={padImage}
        quality={100}
        loading={imageLoading}
        responsiveOptions={{
          lg: { height: 768, pad: padImage, width: 768 },
          sm: { height: 576, pad: padImage, width: 576 }
        }}
      />
    </SquareImageSkeleton>
  );
};

const SquareImageSkeleton = ({ children }: { children?: JSX.Element }) => {
  return (
    <div className="flex aspect-square w-full items-center justify-center bg-white">
      {children}
    </div>
  );
};

const CardInteraction = ({
  product,
  allowNotifications,
  pagePosition
}: {
  allowNotifications: boolean;
  pagePosition: PAGE_POSITIONS;
  product: ProductListItem;
}) => {
  const notificationsData = {
    id: product.id,
    image: product.image,
    legacyId: product.legacy_id,
    name: product.name,
    price: product.price.sale ?? product.price.regular,
    queryID: product.queryID
  };

  if (allowNotifications) {
    return (
      <div className="absolute right-2 top-[12px] lg:right-4 lg:top-[20px]">
        <ProductNotificationButton
          product={notificationsData}
          pagePosition={pagePosition}
          buttonStyle="compact"
        />
      </div>
    );
  }

  return null;
};

export default {
  External: ExternalProductCard,
  Internal: InternalProductCard
};
